import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import styled from 'styled-components';

import {
  GlassButton,
  GlassButtonWrapper,
  GlassDimOverlay,
} from '../common/GlassHoverOverlay';
import { MediaWithErrorHandling } from '../common/MediaWithErrorHandling';
import SpinningLoading from '../SpinningLoading';

import type { ElementState } from '../../renderer/ElementState';
import type { Story } from '../../types.ts/story';
import type { FC } from 'react';

interface ImagePresetProps {
  activeElement: ElementState;
  parentCompositionElement?: ElementState;
  url: string;
  story?: Story;
  onSourceChange?: (newUrl: string) => void;
  isImageControl?: boolean;
  isLogoControl?: boolean;
  isVideoControl?: boolean;
  isHeadshotControl?: boolean;
}

export const ImagePreset: FC<ImagePresetProps> = props => {
  const videoCreator = useVideoCreatorStore();

  // videoCreator.getImageElement doesn't check if the argument is an image element,
  // it just checks if it is a composition element and gets a child image. if it's not
  // a composition, it returns the element itself. THEREFORE, this works for video
  // elements too, which is why it's working in VideoControl. We should make
  // this cleaner later.
  //
  const currentElement = videoCreator.getImageElement(props.activeElement);
  const url = currentElement.source.source;

  const getDescription = () => {
    if (currentElement.source.type === 'image') {
      const photoAssets = [
        ...(videoCreator.story?.storyArtifacts || []),
        ...(videoCreator.story?.storyAssets || []),
        ...(videoCreator.story?.aiPhotos || []),
        ...(videoCreator.organization?.organizationArtifacts || []),
        ...(videoCreator.organization?.organizationLogos || []),
      ];
      return photoAssets.find(
        p =>
          p.responsiveImage?.srcSet === url ||
          p.responsiveImage?.src === url ||
          p.url === url,
      )?.title;
    }
    return '';
  };

  const origin =
    props.isImageControl || props.isLogoControl || props.isVideoControl
      ? 'composition-settings'
      : undefined;

  // Logos sit whole on a neutral backing; photos fill the tile.
  const isLogo =
    !!props.isLogoControl || videoCreator.isLogoElement(props.activeElement);
  const replaceNoun = props.isVideoControl
    ? 'video'
    : props.isHeadshotControl
      ? 'headshot'
      : isLogo
        ? 'logo'
        : 'image';

  const openReplace = () => {
    videoCreator.openPhotoElementReplacementModal = {
      element: props.activeElement,
      parentCompositionElement: props.parentCompositionElement,
      onImageSourceChange: props.onSourceChange,
      isLogoControl: props.isLogoControl,
      isVideoControl: props.isVideoControl || false,
      isHeadshotControl: props.isHeadshotControl,
      // No title: the modal derives "Replace Logo" / "Replace Video"
      // / "Replace Headshot" / "Replace Image" from these flags.
      origin,
    };
  };

  return (
    <>
      <Main>
        {videoCreator.savingStockPhoto && (
          <SpinningLoading text="Saving stock image…" />
        )}
        {/* The whole thumbnail is the Replace button; the glass label shows
            on hover and keyboard focus, the media-card treatment. It used to
            sit permanently over a dimmed image (PLAT-12137). */}
        <ImageWrapper
          type="button"
          aria-label={`Replace ${replaceNoun}`}
          onClick={openReplace}
        >
          {props.isVideoControl ? (
            <MediaWithErrorHandling as={Video} src={props.url} />
          ) : (
            <MediaWithErrorHandling
              as={Image}
              src={url}
              alt=""
              $contain={isLogo}
            />
          )}
          <Reveal aria-hidden="true">
            <GlassDimOverlay />
            <GlassButtonWrapper>
              <GlassButton>Replace</GlassButton>
            </GlassButtonWrapper>
          </Reveal>
        </ImageWrapper>

        <Description>{getDescription()}</Description>
      </Main>
    </>
  );
};

const Main = styled.div`
  position: relative;
  // margin: 15px 0;
  // height: 80px;
  // background-size: contain;
  // background-repeat: no-repeat;
  // background-position: center;
  border-radius: var(--radius-input);
  display: flex;
  gap: 10px;
  align-items: center;
`;

const Image = styled.img<{ $contain?: boolean }>`
  display: block;
  width: 107px;
  height: 70px;
  box-sizing: border-box;
  border-radius: inherit;
  object-fit: ${props => (props.$contain ? 'contain' : 'cover')};
  padding: ${props => (props.$contain ? '6px' : '0')};
  background: ${props => (props.$contain ? 'var(--surface)' : 'none')};

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 40px;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0),
      rgba(0, 0, 0, 50%)
    );
  }
`;

const Video = styled.video`
  display: block;
  width: 107px;
  height: 70px;
  border-radius: inherit;
  object-fit: cover;

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 40px;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0),
      rgba(0, 0, 0, 50%)
    );
  }
`;

const ImageWrapper = styled.button`
  position: relative;
  flex-shrink: 0;
  width: 107px;
  height: 70px;
  padding: 0;
  border: none;
  border-radius: var(--radius);
  background: none;
  overflow: hidden;
  cursor: pointer;
  outline: none;

  &:focus-visible {
    box-shadow: 0 0 0 2px var(--focus-ring);
  }
`;

/** The glass Replace label, shown while the thumbnail is hovered or focused. */
const Reveal = styled.span`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  opacity: 0;
  transition: opacity var(--transition-fast);

  ${ImageWrapper}:hover &,
  ${ImageWrapper}:focus-visible & {
    opacity: 1;
  }
`;

const Description = styled.span`
  color: var(--text-secondary);
  font-weight: var(--font-weight-light);
  font-size: var(--text-xs);
  line-height: 12px;
`;
